import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { ArrowLeft, ExternalLink, Radio, Calendar, Users, Bookmark, BookmarkCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTimezone } from "@/contexts/TimezoneContext";
import { convertShowTimes, BASE_TZ, zoneLabel } from "@/lib/timezone";
import TimezoneSelector from "@/components/TimezoneSelector";
import ShowRatingBox from "@/components/ShowRatingBox";
import MembershipTiers from "@/components/MembershipTiers";
import FreshyDefcon from "@/components/FreshyDefcon";
import ShowMerch from "@/components/ShowMerch";
import { CollabBadge, COLLAB_NOTE } from "@/components/CollabBadge";
import { toast } from "sonner";

const DAY_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const CATEGORY_COLORS = {
  Talk: "#007AFF", Music: "#FF3B30", Sports: "#32D74B", Podcast: "#FFD60A",
  News: "#AF52DE", Comedy: "#FF9500", Interview: "#5AC8FA", "Live Event": "#FF2D92",
};
const categoryColor = (c) => CATEGORY_COLORS[c] || "#FF3B30";

function airingDays(s) {
  if (s.is_special && s.special_frequency === "daily") return [0, 1, 2, 3, 4, 5, 6];
  if (Array.isArray(s.days_of_week) && s.days_of_week.length > 0) return s.days_of_week;
  return [s.day_of_week ?? 0];
}

function initials(name) {
  return name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

export default function ShowDetail() {
  const { id } = useParams();
  const [show, setShow] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { timezone, isBase } = useTimezone();
  const { user, refresh } = useAuth();
  const [savingSave, setSavingSave] = useState(false);
  const isSaved = !!(user && user.saved_shows && user.saved_shows.includes(id));

  const toggleSave = async () => {
    if (!user) {
      toast("Sign in to save shows", { description: "Continue with Discord to build your watchlist." });
      return;
    }
    setSavingSave(true);
    try {
      if (isSaved) {
        await api.delete(`/user/saved-shows/${id}`);
      } else {
        await api.post(`/user/saved-shows/${id}`);
      }
      await refresh();
      toast.success(isSaved ? "Removed from watchlist" : "Saved to your watchlist");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setSavingSave(false);
    }
  };

  // Record a "watch" event. Called manually from the Mark Watched button and
  // automatically when the user clicks the YouTube live link.
  const markWatched = async (source = "manual") => {
    if (!user) {
      if (source === "manual") {
        toast("Sign in to log watches", { description: "Streaks & badges track your saved-show habit." });
      }
      return;
    }
    try {
      const { data } = await api.post(`/user/watched/${id}`, { source });
      if (data.counted) {
        toast.success(source === "auto" ? "Watch counted 🔥" : "Marked as watched 🔥");
      } else if (source === "manual") {
        toast("Already counted today", { description: "Come back next week to grow your streak." });
      }
    } catch {}
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/shows/${id}`);
        setShow(data);
      } catch {
        setNotFound(true);
      }
    })();
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-24" data-testid="show-not-found">
        <h1 className="font-display text-5xl mb-4">SHOW NOT FOUND.</h1>
        <p className="text-[var(--ff-text-dim)] mb-8">We couldn't find that show on the network.</p>
        <Link to="/shows" className="btn-primary"><ArrowLeft size={14} /> Back to Shows</Link>
      </div>
    );
  }
  if (!show) return <div className="p-12 text-[var(--ff-text-dim)]">Loading...</div>;

  const color = categoryColor(show.category);
  const baseDays = airingDays(show);
  // Convert anchor day + times to selected timezone. Preserve relative day pattern.
  let days = baseDays;
  let startT = show.start_time;
  let endT = show.end_time;
  if (!isBase && show.start_time && show.end_time) {
    const anchor = baseDays[0];
    const conv = convertShowTimes(anchor, show.start_time, show.end_time, timezone);
    const shift = ((conv.day - anchor) % 7 + 7) % 7;
    days = baseDays.map((d) => (d + shift) % 7).sort((a, b) => a - b);
    startT = conv.start_time;
    endT = conv.end_time;
  }

  return (
    <div data-testid="show-detail-page" className="max-w-[1600px] mx-auto px-6 lg:px-12 py-24">
      <Link to="/shows" className="inline-flex items-center gap-2 label-eyebrow mb-8 hover:text-[var(--ff-red)]" data-testid="back-to-shows">
        <ArrowLeft size={12} /> BACK TO SHOWS
      </Link>

      {/* Hero */}
      <div className="grid lg:grid-cols-12 gap-12 mb-20">
        <div className="lg:col-span-5">
          <div className="relative aspect-square overflow-hidden" style={{ background: `linear-gradient(135deg, ${color}44 0%, ${color}11 100%)` }}>
            {show.thumbnail_url ? (
              <img src={show.thumbnail_url} alt={show.title} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="font-display text-[10rem] leading-none opacity-30" style={{ color }}>
                  {show.title.split(" ").map((w) => w[0]).slice(0, 3).join("")}
                </div>
              </div>
            )}
            <div className="absolute top-4 left-4 flex flex-wrap gap-2">
              {show.is_special && (
                <span className="font-mono-ui text-xs uppercase tracking-[0.15em] px-2 py-1 bg-[#AF52DE] text-white font-bold" data-testid="detail-special-badge">
                  ✦ {show.special_duration_hours || ""}-HOUR {show.special_frequency === "daily" ? "DAILY " : show.special_frequency === "weekly" ? "WEEKLY " : ""}SPECIAL
                </span>
              )}
              {show.is_premiere && <span className="font-mono-ui text-xs uppercase tracking-[0.15em] px-2 py-1 bg-[#FFD60A] text-black font-bold" data-testid="detail-premiere-badge">★ PREMIERE</span>}
              {show.is_finale && <span className="font-mono-ui text-xs uppercase tracking-[0.15em] px-2 py-1 bg-[var(--ff-red)] text-white font-bold" data-testid="detail-finale-badge">⬢ SEASON FINALE</span>}
              {show.is_collab && <CollabBadge size="lg" solid testId="detail-collab-badge" />}
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          {show.category && (
            <div className="mb-4">
              <span className="font-mono-ui text-xs uppercase tracking-[0.2em] px-3 py-1.5 border font-bold" style={{ background: color, color: "white", borderColor: color }} data-testid="detail-category">
                {show.category}
              </span>
            </div>
          )}
          <h1 className="font-display text-5xl lg:text-7xl mb-6" data-testid="detail-title">{show.title}</h1>
          {show.is_collab && (
            <div className="mb-6 inline-flex items-start gap-3 border border-[#5AC8FA]/50 bg-[#5AC8FA]/10 px-4 py-3 max-w-3xl" data-testid="detail-collab-note">
              <CollabBadge testId="detail-collab-note-badge" />
              <p className="text-sm text-[var(--ff-text-dim)]"><span className="text-white">{COLLAB_NOTE}</span> This show is a partner collaboration and isn&apos;t owned by Freshy Fresh Productions LLC, so it isn&apos;t ranked on the leaderboard.</p>
            </div>
          )}
          {show.description && <p className="text-lg text-[var(--ff-text-dim)] leading-relaxed max-w-3xl mb-8" data-testid="detail-description">{show.description}</p>}

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div>
              <div className="label-eyebrow mb-2 flex items-center gap-2"><Calendar size={12} /> AIRS</div>
              <div className="font-mono-ui text-sm">
                {days.map((d) => DAY_FULL[d]).join(" · ")}
              </div>
              <div className="font-display text-2xl mt-1" data-testid="detail-times">{startT}–{endT}</div>
              <div className="mt-3 flex flex-col gap-2">
                <TimezoneSelector />
                {!isBase && (
                  <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.15em] text-[var(--ff-text-dim)]" data-testid="detail-tz-note">
                    Shown in <span className="text-white">{zoneLabel(timezone)}</span> · Base: US Eastern
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-2 flex items-center gap-2"><Radio size={12} /> STATUS</div>
              <div className="font-mono-ui text-sm space-y-1">
                <div>{show.is_recurring ? "↻ RECURRING WEEKLY" : "◆ ONE-OFF AIRING"}</div>
                {show.is_special && show.special_duration_hours && (
                  <div className="text-[#AF52DE]">
                    ✦ {show.special_duration_hours}-HOUR
                    {show.special_frequency === "daily" ? " DAILY" : show.special_frequency === "weekly" ? " WEEKLY" : ""} SPECIAL
                  </div>
                )}
                {show.is_premiere && show.premiere_date && <div className="text-[#FFD60A]">★ Premiere: {show.premiere_date}</div>}
                {show.is_finale && show.finale_date && <div className="text-[var(--ff-red)]">⬢ Finale: {show.finale_date}</div>}
              </div>
            </div>
          </div>

          {show.season_number && (
            <div className="surface-card p-6 mb-8" data-testid="detail-season">
              <div className="label-eyebrow mb-2 text-[var(--ff-red)]">▮ SEASON {show.season_number}</div>
              {show.season_start && show.season_end && (
                <div className="font-mono-ui text-sm">
                  <span className="text-[var(--ff-text-dim)]">Runs from</span> <span className="font-display text-lg">{show.season_start}</span>
                  <span className="text-[var(--ff-text-dim)]"> to </span> <span className="font-display text-lg">{show.season_end}</span>
                </div>
              )}
            </div>
          )}

          {show.youtube_url && (
            <a href={show.youtube_url} target="_blank" rel="noreferrer" onClick={() => markWatched("auto")} className="btn-primary" data-testid="detail-youtube-btn">
              <ExternalLink size={14} /> Watch on YouTube
            </a>
          )}
          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <button
              onClick={toggleSave}
              disabled={savingSave}
              className={`inline-flex items-center gap-2 px-4 py-2.5 border font-mono-ui text-xs uppercase tracking-[0.15em] transition-colors ${isSaved ? "bg-[var(--ff-red)] text-white border-[var(--ff-red)]" : "border-[var(--ff-border)] text-white hover:border-[var(--ff-red)]"}`}
              data-testid="detail-save-btn"
            >
              {isSaved ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
              {isSaved ? "Saved to Watchlist" : "Save Show"}
            </button>
            {show.start_time && show.end_time && (
              <a
                href={`${API_BASE}/shows/${show.id}/ics`}
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-[var(--ff-border)] text-white hover:border-white font-mono-ui text-xs uppercase tracking-[0.15em] transition-colors"
                data-testid="detail-ics-btn"
              >
                <Calendar size={14} /> Add to Calendar (.ics)
              </a>
            )}
            {user && (
              <button
                onClick={() => markWatched("manual")}
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-[var(--ff-border)] text-white hover:border-[#FFD60A] hover:text-[#FFD60A] font-mono-ui text-xs uppercase tracking-[0.15em] transition-colors"
                data-testid="detail-watched-btn"
              >
                🔥 Mark Watched
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Freshy Defcon (only renders on the show it's bound to — Crashout) */}
      <FreshyDefcon showId={show.id} />

      {/* Fan Rating */}
      <div className="border-t border-[var(--ff-border)] pt-12 mb-16">
        <ShowRatingBox showId={show.id} />
      </div>

      {/* Membership Tiers */}
      <div className="border-t border-[var(--ff-border)] pt-12 mb-16">
        <MembershipTiers showId={show.id} />
      </div>

      <ShowMerch showId={show.id} className="border-t border-[var(--ff-border)] pt-12 mb-16" />

      {/* Hosts */}
      {show.hosts && show.hosts.length > 0 && (
        <div className="border-t border-[var(--ff-border)] pt-12">
          <div className="flex items-center gap-3 mb-8">
            <Users className="text-[var(--ff-red)]" size={24} />
            <h2 className="font-display text-4xl">Hosted By</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--ff-border)]">
            {show.hosts.map((h) => (
              <Link key={h} to={`/hosts/${encodeURIComponent(h)}`} className="bg-[var(--ff-surface)] p-6 hover:bg-[var(--ff-surface-hover)] transition-colors flex items-center gap-4" data-testid={`detail-host-${h.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="w-14 h-14 bg-[var(--ff-red)] text-white flex items-center justify-center font-display text-lg">
                  {initials(h)}
                </div>
                <div>
                  <div className="font-display text-xl">{h}</div>
                  <div className="label-eyebrow text-[var(--ff-red)] mt-1">VIEW PROFILE →</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
